import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import TextInput from "../components/common/TextInput";
import Button from "../components/common/Button";
import Meta from "../components/Meta";
import colors from "../utils/constants/colors";

/* =============================== Styling only =============================== */

const AuthBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #f1f5fb 60%, #eef3fb 100%);
  padding-top: 64px; /* space for NavBar */
`;

const ForgotPasswordContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e9eef6;
  border-radius: 16px;
  margin: 48px auto;
  width: 460px;
  max-width: 95%;
  padding: 36px 32px;
  box-shadow: 0 12px 28px rgba(2, 8, 23, 0.06), 0 2px 8px rgba(2, 8, 23, 0.04);
`;

const Header = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
`;

const SubHeader = styled.p`
  margin: 0 0 24px 0;
  text-align: center;
  font-size: 14px;
  color: #64748b;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 18px;
  font-size: 14px;
  color: #475569;

  .link {
    color: ${colors.primary};
    font-weight: 700;
    cursor: pointer;
    margin-left: 6px;
  }
`;

const ErrorMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 8px 12px;
`;

const SuccessMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #166534;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 8px 12px;
`;

/* =============================== Component =============================== */

const ForgotPassword = (props) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setErrorMsg(""); // Clear error when user types
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      setErrorMsg("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");

    try {
      const result = await request("POST", API_CONSTANT_MAP.forgot_password, {
        email,
      });
      if (result.data.success) {
        setIsEmailSent(true);
      }
    } catch (error) {
      setErrorMsg(error.error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignIn = () => navigate("/signin");
  
  const handleResendEmail = () => {
    setIsEmailSent(false);
    setEmail("");
    setErrorMsg("");
  };

  return (
    <Meta pageMeta={{ title: "Forgot Password - Doitly" }}>
      <NavBar user={props.state.user} />
      <AuthBg>
        <OuterContainer>
          <ForgotPasswordContainer>
            <Header>Reset your password</Header>
            
            {!isEmailSent ? (
              <>
                <SubHeader>
                  Enter your email address and we'll send you instructions to reset your password
                </SubHeader>

                <form onSubmit={handleVerifyEmail}>
                  <TextInput
                    value={email}
                    name="email"
                    label="Email Address"
                    onChange={handleEmailChange}
                    type="email"
                    placeholder="Enter your email address"
                  />

                  <Button
                    type="submit"
                    style={{
                      margin: "18px 0 10px",
                      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                      borderRadius: "10px",
                      fontWeight: 800,
                    }}
                    disabled={isLoading}
                  >
                    <span>{isLoading ? "Sending..." : "Send Reset Instructions"}</span>
                  </Button>

                  {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
                </form>
              </>
            ) : (
              <>
                <SubHeader>
                  We've sent password reset instructions to <strong>{email}</strong>
                </SubHeader>
                
                <SuccessMessage>
                  Please check your email and follow the instructions to reset your password.
                </SuccessMessage>

                <Button
                  onClick={handleResendEmail}
                  style={{
                    margin: "18px 0 10px",
                    background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                    borderRadius: "10px",
                    fontWeight: 800,
                  }}
                >
                  <span>Send Another Email</span>
                </Button>
              </>
            )}

            <Footer>
              <span>Remember your password?</span>
              <span className="link" onClick={handleBackToSignIn}>
                Back to Sign In
              </span>
            </Footer>
          </ForgotPasswordContainer>
        </OuterContainer>
      </AuthBg>
    </Meta>
  );
};

const mapStateToProps = (state) => ({ state });

export default connect(mapStateToProps)(ForgotPassword);
